import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Edge, Node } from 'reactflow'
import chevronDoubleRightIcon from '@/assets/icons/chevron-double-right.png'
import { MaterialIcon } from '@/ui/material-icon'
import type { RegisteredSourcePickerItem as RegisteredDestinationPickerItem } from '@/features/adp/hooks/useRegisteredDataSources'
import {
  ADP_DATA_SOURCES_FILTER_COLUMN_TARGET_DESTINATION,
  createAdpPhysicalTables,
  postAdpDataFlowMapping,
  createAdpTargetTablesBatch,
  createAdpTargetDataSource,
  fetchAdpDataSourcesFilter,
  testAdpConnection,
} from '@/features/adp/api/adpDataSources.api'
import type {
  AdpConnectionTestDatabasePayload,
  AdpConnectionTestOptionField,
  AdpPhysicalTablesBody,
  AdpTargetTablesBatchBody,
} from '@/features/adp/api/adpDataSources.types'
import type { AdpAssetColumnEntry } from '@/features/adp/utils/adpPipelineSessionStorage'
import {
  readAdpPipelineSession,
  syncUpstreamSourcePrimaryKeyFromDestinationPreview,
  upsertAssetSession,
  upsertColumnMappingSession,
  upsertFullOperationJsonSession,
  useAdpPipelineSessionSnapshot,
} from '@/features/adp/utils/adpPipelineSessionStorage'
import styles from './ConfigureDataDestinationSheet.module.scss'
import { PrerequisitesModal } from '@/shared/components/PrerequisitesModal/PrerequisitesModal'
import { validateFormFields } from '@/features/adp/utils/validateSchemaField'
import { toast } from 'sonner'
import { DynamicDestinationFields } from './DynamicDestinationFields'
import { RegisteredDestinationTablesFields } from './RegisteredDestinationTablesFields'
import type { RegisteredDestinationConnectionDetails } from './RegisteredDestinationTablesFields'
import { LoadTypeSettings } from './LoadTypeSettings'
import type {
  DynamicSchemaDefinition,
  DynamicSchemaField,
} from './ConfigureDataDestinationSheet.types'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/accordion'
import {
  buildConnectionDetailsFromRegisteredListItem,
  extractHostPortFromEndpoint,
  extractSchemaNameFromEndpoint,
  firstNonEmptyTrimmed,
  firstUsableConnectionString,
  getSafeString,
  getTargetTableType,
  normalizePortForDatabasePayload,
  normalizePortString,
  resolveDataSourceName,
  resolveGlueTableName,
  supportsSchemaForInstance,
} from './helpers'
import { Button } from '@/ui/button'

type ConfigureDataDestinationSheetProps = {
  onClose: () => void
  onBackToChoose?: () => void
  onSavePipelineDefinition?: () => Promise<void> | void
  onColumnMappingSaved?: () => void
  dataFlowId?: number | null
  configuringNodeId?: string | null
  registeredDestination?: RegisteredDestinationPickerItem | null
  allowedInstance?: string | null
  graphSnapshot?: { nodes: Node[]; edges: Edge[] }
  destinationTitle?: string
  destinationTypeLabel?: string
  connectorImage?: string
}

type LoadMode = 'full' | 'incremental'
type PhysicalTableChoice = 'yes' | 'no'
type OutputPreviewRow = {
  id: string
  fieldName: string
  fieldDataType: string
  fieldLength: number
  isSelected: boolean
  isPrimaryKey: boolean
}

type ColumnMappingRow = {
  sourceName: string
  sourceColumn: string
  sourceDataType: string
  destinationColumn: string
  destinationDataType: string
  /** From upstream session — pre-checks output preview "Primary key" for that column. */
  destinationIsBusinessKey: boolean
}
type ScdType = 'scd_1' | 'scd_2'
type DestinationCategory = 'database' | 'filesystem' | 'nosql' | 'rest'
type RegisteredTableSelectionMode = 'existing' | 'new' | null
type DestinationDraftState = {
  form?: Record<string, string>
  responseSelections?: Record<string, string>
  dynamicConfigFields?: DynamicSchemaField[]
  registeredTableSelectionMode?: RegisteredTableSelectionMode
  registeredConnectionDetails?: RegisteredDestinationConnectionDetails | null
  loadMode?: LoadMode
  scdType?: ScdType
  deleteIdentifierColumn?: string
  createdDateColumn?: string
  modifiedDateColumn?: string
  /** True after the user saved table selection on a registered destination. */
  destinationTableStepComplete?: boolean
  /** User preference: call create-physical-tables when saving output preview (DB targets only). */
  createPhysicalTable?: PhysicalTableChoice
  /** From registered-tables API when user picked an existing table — avoids target-tables batch. */
  registeredTargetDataSourceId?: number
  registeredTargetTableId?: number
}

const OUTPUT_PREVIEW_DATA_TYPES = [
  'string',
  'bigint',
  'boolean',
  'date',
  'float',
  'int',
  'timestamp',
]

function normalizeOutputDataType(raw: string): string {
  const normalized = raw.trim().toLowerCase()
  if (!normalized) return 'string'
  if (/^(u?varchar|varchar|nvarchar|char|nchar)\s*(\(\s*\d+\s*\))?$/.test(normalized))
    return 'string'
  if (normalized === 'text') return 'string'
  if (/^(integer|int|int4)$/.test(normalized)) return 'int'
  if (/^(bigint|int8)$/.test(normalized)) return 'bigint'
  if (/^(float|real|double|double precision|decimal|numeric)\b/.test(normalized)) return 'float'
  if (normalized === 'boolean' || normalized === 'bool') return 'boolean'
  if (normalized === 'date') return 'date'
  if (/^(datetime|timestamp|timestamptz)$/.test(normalized)) return 'timestamp'
  return normalized
}

function extractStringLengthFromRawDataType(raw: string): number | null {
  const normalized = raw.trim().toLowerCase()
  const m = normalized.match(/^(u?varchar|varchar|nvarchar|char|nchar)\s*\(\s*(\d+)\s*\)$/)
  if (!m) return null
  const n = Number(m[2])
  if (!Number.isFinite(n) || n < 0) return null
  return n
}

function defaultFieldLengthForDataType(rawOrNormalizedType: string): number {
  // If the backend sends `varchar(50)` style types, we keep the length in the UI/payload.
  const extracted = extractStringLengthFromRawDataType(rawOrNormalizedType)
  if (extracted !== null) return extracted

  const base = normalizeOutputDataType(rawOrNormalizedType)
  return base === 'string' ? 1024 : 0
}

function normalizeTableName(value: string): string {
  return (
    value
      .trim()
      .replace(/[^a-zA-Z0-9_]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '') || 'target_table'
  )
}

function resolveDestinationCategory(
  dataSourceType: string,
  instanceType: string
): DestinationCategory {
  const probe = `${dataSourceType} ${instanceType}`.toLowerCase()
  if (/\b(file|filesystem|object|s3|blob|adl|hdfs)\b/.test(probe)) return 'filesystem'
  if (/\b(nosql|mongo|cassandra|redis|neo4j|elasticsearch|druid)\b/.test(probe)) return 'nosql'
  if (/\b(rest|api|http)\b/.test(probe)) return 'rest'
  return 'database'
}

function buildTargetPath(
  category: DestinationCategory,
  payload: Record<string, unknown>
): string {
  let candidateKeys: string[];

  if (category === 'filesystem') {
    candidateKeys = ['path', 'file_path', 'folder_path', 'directory', 'bucket', 'container', 'warehouse'];
  } else if (category === 'nosql') {
    candidateKeys = ['database', 'schema', 'collection', 'bucket'];
  } else if (category === 'rest') {
    candidateKeys = ['base_url', 'path', 'endpoint'];
  } else {
    candidateKeys = ['schema'];
  }

  const out: Record<string, string> = {};

  candidateKeys.forEach((key) => {
    const value = payload[key];
    if (typeof value !== 'string') return;

    const trimmed = value.trim();
    if (!trimmed) return;

    out[key] = trimmed;
  });

  return Object.keys(out).length ? JSON.stringify(out) : '';
}

function toUniquePositiveIds(values: Array<number | undefined>): number[] {
  return [
    ...new Set(values.filter((value): value is number => Number.isFinite(value) && value > 0)),
  ]
}

function getDestinationDraftStorageKey(nodeId: string): string {
  return `adp:destination:draft:${nodeId}`
}

function readDestinationDraft(nodeId: string | null): DestinationDraftState | null {
  const normalizedNodeId = nodeId?.trim()
  if (!normalizedNodeId) return null
  const key = getDestinationDraftStorageKey(normalizedNodeId)
  try {
    const raw = globalThis.sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as DestinationDraftState
  } catch {
    return null
  }
}

function mergeDestinationFormWithSessionAsset(
  nodeId: string | null,
  draft: DestinationDraftState | null
): Record<string, string> {
  const base = draft?.form && typeof draft.form === 'object' ? { ...draft.form } : {}
  const n = nodeId?.trim()
  if (!n) return base
  const asset = readAdpPipelineSession().assets[n]
  if (asset?.assetKind === 'destination' && asset.tableName?.trim() && !base.table_name?.trim()) {
    return { ...base, table_name: asset.tableName.trim() }
  }
  return base
}

export function ConfigureDataDestinationSheet({
  onClose,
  onBackToChoose,
  onSavePipelineDefinition,
  onColumnMappingSaved,
  dataFlowId = null,
  configuringNodeId = null,
  registeredDestination = null,
  allowedInstance = null,
  graphSnapshot = { nodes: [], edges: [] },
  destinationTitle = 'Destination',
  destinationTypeLabel = 'Postgres_destination_123',
  connectorImage = '/adp-databases/postgresql.svg',
}: Readonly<ConfigureDataDestinationSheetProps>) {
  const initialDestinationDraft = useMemo(
    () => readDestinationDraft(configuringNodeId),
    [configuringNodeId]
  )
  const [form, setForm] = useState<Record<string, string>>(() =>
    mergeDestinationFormWithSessionAsset(configuringNodeId, initialDestinationDraft)
  )
  const [dynamicConfigFields, setDynamicConfigFields] = useState<DynamicSchemaField[]>(
    () => initialDestinationDraft?.dynamicConfigFields ?? []
  )
  const [isDynamicConfigLoading, setIsDynamicConfigLoading] = useState(false);
  const [destinationDetailsAccordionValue, setDestinationDetailsAccordionValue] = useState<
    'destination-details' | ''
  >(() => (initialDestinationDraft?.destinationTableStepComplete ? '' : 'destination-details'))
  const [loadTypeOpen, setLoadTypeOpen] = useState(false);
  const [outputPreviewOpen, setOutputPreviewOpen] = useState(false);
  const [columnMappingOpen, setColumnMappingOpen] = useState(false);
  const [loadMode, setLoadMode] = useState<LoadMode>(
    () => initialDestinationDraft?.loadMode ?? 'full'
  )
  const [registeredTableSelectionMode, setRegisteredTableSelectionMode] = useState<
    RegisteredTableSelectionMode
  >(() => initialDestinationDraft?.registeredTableSelectionMode ?? null)
  const [registeredDestinationConnectionDetails, setRegisteredDestinationConnectionDetails] =
    useState<RegisteredDestinationConnectionDetails | null>(
      () => initialDestinationDraft?.registeredConnectionDetails ?? null
    )
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testResponseFields, setTestResponseFields] = useState<AdpConnectionTestOptionField[]>([])
  const [responseSelections, setResponseSelections] = useState<Record<string, string>>(
    () => initialDestinationDraft?.responseSelections ?? {}
  )
  const [isSubmittingTarget, setIsSubmittingTarget] = useState(false)
  const [outputPreviewRows, setOutputPreviewRows] = useState<OutputPreviewRow[]>([])
  const [scdType, setScdType] = useState<ScdType>(() => initialDestinationDraft?.scdType ?? 'scd_1')
  const [deleteIdentifierColumn, setDeleteIdentifierColumn] = useState(
    () => initialDestinationDraft?.deleteIdentifierColumn ?? ''
  )
  const [createdDateColumn, setCreatedDateColumn] = useState(
    () => initialDestinationDraft?.createdDateColumn ?? ''
  )
  const [modifiedDateColumn, setModifiedDateColumn] = useState(
    () => initialDestinationDraft?.modifiedDateColumn ?? ''
  )
  const [createPhysicalTableChoice, setCreatePhysicalTableChoice] = useState<PhysicalTableChoice>(
    () => initialDestinationDraft?.createPhysicalTable ?? 'yes'
  )
  const [registeredExistingTableIds, setRegisteredExistingTableIds] = useState<{
    targetDataSourceId: number
    targetTableId: number
  } | null>(() => {
    const d = initialDestinationDraft
    if (
      typeof d?.registeredTargetDataSourceId === 'number' &&
      typeof d?.registeredTargetTableId === 'number'
    ) {
      return {
        targetDataSourceId: d.registeredTargetDataSourceId,
        targetTableId: d.registeredTargetTableId,
      }
    }
    return null
  })
  const [isPublishingTarget, setIsPublishingTarget] = useState(false)
  const [prerequisiteData, setPrerequisiteData] = useState<unknown>(null)
  const [prereqModalOpen, setPrereqModalOpen] = useState(false)
  const [hasHydratedDraft, setHasHydratedDraft] = useState<boolean>(() => {
    const d = initialDestinationDraft
    if (!d) return false
    if (d.form && Object.keys(d.form).length > 0) return true
    if (d.responseSelections && Object.keys(d.responseSelections).length > 0) return true
    if (d.dynamicConfigFields && d.dynamicConfigFields.length > 0) return true
    if (d.registeredTableSelectionMode) return true
    if (d.registeredConnectionDetails) return true
    if (d.destinationTableStepComplete) return true
    if (d.loadMode && d.loadMode !== 'full') return true
    if (d.scdType && d.scdType !== 'scd_1') return true
    if (d.deleteIdentifierColumn?.trim() || d.createdDateColumn?.trim() || d.modifiedDateColumn?.trim())
      return true
    return false
  })
  const destinationDraftKey = useMemo(
    () => (configuringNodeId?.trim() ? getDestinationDraftStorageKey(configuringNodeId) : null),
    [configuringNodeId]
  )

  // ─── Validation errors for dynamic form fields ──────────────────────────────
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const isNonDbDestination = useMemo(() => {
    const type = (registeredDestination?.dataSourceType ?? '')
      .trim()
      .toUpperCase()
      .replaceAll(/\s+/g, ' ')

    if (type) {
      if (type === 'RDBMS' || type === 'NO SQL' || type === 'NOSQL') {
        return false
      }
      return true
    }

    // Unregistered flow: `dataSourceType` is empty — infer from the connector / node (was wrongly non-DB and hid Load Type + DB mapping).
    const instanceHint = firstNonEmptyTrimmed(
      allowedInstance,
      destinationTypeLabel,
      registeredDestination?.dataSourceInstance
    )
    if (!instanceHint) {
      return false
    }
    return resolveDestinationCategory('', instanceHint) !== 'database'
  }, [
    allowedInstance,
    destinationTypeLabel,
    registeredDestination?.dataSourceInstance,
    registeredDestination?.dataSourceType,
  ])
  const allExpanded = isNonDbDestination
    ? outputPreviewOpen && columnMappingOpen
    : loadTypeOpen && outputPreviewOpen && columnMappingOpen
  const displayTitle = registeredDestination?.connectionName ?? destinationTitle
  const displayType = registeredDestination
    ? `${registeredDestination.dataSourceInstance} (${registeredDestination.dataSourceType})`
    : allowedInstance || destinationTypeLabel
  const displayImage = registeredDestination?.image?.trim() || connectorImage?.trim();

  const destinationName = useMemo(
    () => registeredDestination?.connectionName ?? destinationTypeLabel ?? '',
    [registeredDestination?.connectionName, destinationTypeLabel]
  );
  const isRegisteredDestination = Boolean(registeredDestination);
  const registeredInstanceSupportsSchema = useMemo(
    () => supportsSchemaForInstance(registeredDestination?.dataSourceInstance),
    [registeredDestination?.dataSourceInstance]
  )
  const shouldHideBackToList = isNonDbDestination;

  const normalizedType = useMemo(() => {
    const fromRegistered = registeredDestination?.dataSourceInstance?.trim();
    if (fromRegistered) {
      return fromRegistered.toLowerCase().split(/[_\s-]/)[0];
    }
    if (allowedInstance?.trim()) {
      return allowedInstance.trim().toLowerCase();
    }
  }, [registeredDestination?.dataSourceInstance, allowedInstance])

  // Check if destination is Excel or S3 to hide physical table creation
  const isExcelOrS3Destination = useMemo(() => {
    const type = normalizedType?.toLowerCase() || ''
    const registeredInstance = registeredDestination?.dataSourceInstance?.toLowerCase() || ''
    const allowedInstanceLower = allowedInstance?.toLowerCase() || ''
    const destinationTypeLabelLower = destinationTypeLabel?.toLowerCase() || ''
    
    // Check for Excel patterns
    const isExcel = type === 'excel' || 
                   type === 'xls' || 
                   type === 'xlsx' ||
                   registeredInstance.includes('excel') ||
                   registeredInstance.includes('xls') ||
                   registeredInstance.includes('xlsx') ||
                   allowedInstanceLower.includes('excel') ||
                   allowedInstanceLower.includes('xls') ||
                   allowedInstanceLower.includes('xlsx') ||
                   destinationTypeLabelLower.includes('excel') ||
                   destinationTypeLabelLower.includes('xls') ||
                   destinationTypeLabelLower.includes('xlsx')
    
    // Check for S3 patterns
    const isS3 = type === 's3' ||
                registeredInstance.includes('s3') ||
                allowedInstanceLower.includes('s3') ||
                destinationTypeLabelLower.includes('s3')
    
    return isExcel || isS3
  }, [normalizedType, registeredDestination?.dataSourceInstance, allowedInstance, destinationTypeLabel])

  const showPhysicalTablePrompt = useMemo(
    () =>
      !isNonDbDestination &&
      !(isRegisteredDestination && registeredTableSelectionMode === 'existing') &&
      !isExcelOrS3Destination,
    [isNonDbDestination, isRegisteredDestination, registeredTableSelectionMode, isExcelOrS3Destination]
  )

  const effectivePhysicalTableChoice: PhysicalTableChoice = useMemo(() => {
    if (isNonDbDestination) return 'no'
    if (isRegisteredDestination && registeredTableSelectionMode === 'existing') return 'no'
    if (isExcelOrS3Destination) return 'no'
    return createPhysicalTableChoice
  }, [
    isNonDbDestination,
    isRegisteredDestination,
    registeredTableSelectionMode,
    createPhysicalTableChoice,
    isExcelOrS3Destination,
  ])

  const shouldSkipTestConnection = useMemo(() => {
    if (!isNonDbDestination) return false
    return normalizedType !== 's3'
  }, [isNonDbDestination, normalizedType])

  // Check if any upstream source is Excel to determine button behavior
  const hasExcelSource = useMemo(() => {
    if (!configuringNodeId || !graphSnapshot.edges.length || !graphSnapshot.nodes.length) return false
    
    const sessionSnapshot = readAdpPipelineSession()
    const incomingByTarget = new Map<string, string[]>()
    for (const edge of graphSnapshot.edges) {
      const arr = incomingByTarget.get(edge.target) ?? []
      arr.push(edge.source)
      incomingByTarget.set(edge.target, arr)
    }
    const nodeById = new Map(graphSnapshot.nodes.map(n => [n.id, n]))
    
    const sourceNodeIds = new Set<string>()
    const stack = [configuringNodeId]
    const seen = new Set<string>()
    while (stack.length > 0) {
      const current = stack.pop()!
      if (seen.has(current)) continue
      seen.add(current)
      const parents = incomingByTarget.get(current) ?? []
      for (const parent of parents) {
        const node = nodeById.get(parent)
        const groupId = (node?.data as { groupId?: string } | undefined)?.groupId
        if (groupId === 'sources') sourceNodeIds.add(parent)
        stack.push(parent)
      }
    }

    // Check if any source asset has Excel as data source instance
    for (const sourceNodeId of sourceNodeIds) {
      const asset = sessionSnapshot.assets[sourceNodeId]
      if (asset?.dataSourceInstance) {
        const instance = asset.dataSourceInstance.toLowerCase().trim()
        if (instance === 'excel' || 
            instance === 'xls' || 
            instance === 'xlsx' ||
            instance.includes('excel') || 
            instance.includes('xls') || 
            instance.includes('xlsx')) {
          return true
        }
      }
    }
    
    return false
  }, [configuringNodeId, graphSnapshot.edges, graphSnapshot.nodes])

  // Update createPhysicalTableChoice to 'no' for Excel and S3 destinations
  useEffect(() => {
    if (isExcelOrS3Destination && createPhysicalTableChoice === 'yes') {
      setCreatePhysicalTableChoice('no')
    }
  }, [isExcelOrS3Destination, createPhysicalTableChoice])

  useEffect(() => {
    if (!destinationDraftKey) {
      setHasHydratedDraft(false)
      return
    }
    try {
      const raw = globalThis.sessionStorage.getItem(destinationDraftKey)
      if (!raw) {
        setHasHydratedDraft(false)
        return
      }
      const parsed = JSON.parse(raw) as DestinationDraftState
      let hydrated = false
      if (Array.isArray(parsed.dynamicConfigFields)) {
        setDynamicConfigFields(parsed.dynamicConfigFields)
        hydrated = hydrated || parsed.dynamicConfigFields.length > 0
      }
      if (parsed.form && typeof parsed.form === 'object') {
        setForm(mergeDestinationFormWithSessionAsset(configuringNodeId, parsed))
        hydrated = hydrated || Object.keys(parsed.form).length > 0
      }
      if (parsed.responseSelections && typeof parsed.responseSelections === 'object') {
        setResponseSelections(parsed.responseSelections)
        hydrated = hydrated || Object.keys(parsed.responseSelections).length > 0
      }
      if (parsed.createPhysicalTable === 'yes' || parsed.createPhysicalTable === 'no') {
        setCreatePhysicalTableChoice(parsed.createPhysicalTable)
        hydrated = true
      }
      if (isRegisteredDestination) {
        if (parsed.registeredTableSelectionMode) {
          setRegisteredTableSelectionMode(parsed.registeredTableSelectionMode)
          hydrated = true
        }
        if (parsed.registeredConnectionDetails !== undefined) {
          setRegisteredDestinationConnectionDetails(parsed.registeredConnectionDetails)
          hydrated = hydrated || Boolean(parsed.registeredConnectionDetails)
        }
        if (parsed.loadMode) {
          setLoadMode(parsed.loadMode)
          hydrated = hydrated || parsed.loadMode !== 'full'
        }
        if (parsed.scdType) {
          setScdType(parsed.scdType)
          hydrated = hydrated || parsed.scdType !== 'scd_1'
        }
        if (parsed.deleteIdentifierColumn !== undefined) {
          setDeleteIdentifierColumn(parsed.deleteIdentifierColumn)
        }
        if (parsed.createdDateColumn !== undefined) {
          setCreatedDateColumn(parsed.createdDateColumn)
        }
        if (parsed.modifiedDateColumn !== undefined) {
          setModifiedDateColumn(parsed.modifiedDateColumn)
        }
        if (parsed.destinationTableStepComplete) {
          setDestinationDetailsAccordionValue('')
          hydrated = true
        }
        if (
          typeof parsed.registeredTargetDataSourceId === 'number' &&
          typeof parsed.registeredTargetTableId === 'number'
        ) {
          setRegisteredExistingTableIds({
            targetDataSourceId: parsed.registeredTargetDataSourceId,
            targetTableId: parsed.registeredTargetTableId,
          })
          hydrated = true
        } else if (parsed.registeredTableSelectionMode === 'new') {
          setRegisteredExistingTableIds(null)
        }
      }
      setHasHydratedDraft(hydrated)
    } catch {
      // Ignore malformed draft payloads; user can continue with fresh form.
      setHasHydratedDraft(false)
    }
  }, [configuringNodeId, destinationDraftKey, isRegisteredDestination])

  useEffect(() => {
    if (!destinationDraftKey) return
    try {
      const payload: DestinationDraftState = {
        form,
        responseSelections,
        dynamicConfigFields,
        createPhysicalTable: createPhysicalTableChoice,
        ...(isRegisteredDestination
          ? {
            registeredTableSelectionMode: registeredTableSelectionMode ?? undefined,
            registeredConnectionDetails: registeredDestinationConnectionDetails,
            loadMode,
            scdType,
            deleteIdentifierColumn,
            createdDateColumn,
            modifiedDateColumn,
            destinationTableStepComplete: Boolean(registeredTableSelectionMode),
            ...(registeredTableSelectionMode === 'existing' && registeredExistingTableIds
              ? {
                registeredTargetDataSourceId: registeredExistingTableIds.targetDataSourceId,
                registeredTargetTableId: registeredExistingTableIds.targetTableId,
              }
              : {}),
          }
          : {}),
      }
      globalThis.sessionStorage.setItem(destinationDraftKey, JSON.stringify(payload))
    } catch {
      // Ignore storage write errors (quota/privacy modes) and keep UI responsive.
    }
  }, [
    createPhysicalTableChoice,
    createdDateColumn,
    deleteIdentifierColumn,
    destinationDraftKey,
    dynamicConfigFields,
    form,
    isRegisteredDestination,
    loadMode,
    modifiedDateColumn,
    registeredDestinationConnectionDetails,
    registeredExistingTableIds,
    registeredTableSelectionMode,
    responseSelections,
    scdType,
  ])

  useEffect(() => {
    let active = true
    const instance = (registeredDestination?.dataSourceInstance || allowedInstance || '')
      .trim()
      .toLowerCase()
    if (!instance) {
      setIsDynamicConfigLoading(false)
      setDynamicConfigFields([])
      return
    }
    const loadSchema = async () => {
      setIsDynamicConfigLoading(true)
      try {
        const dataSources = await fetchAdpDataSourcesFilter({
          column_name: ADP_DATA_SOURCES_FILTER_COLUMN_TARGET_DESTINATION,
          value: true,
          show_schema_definition: true,
          show_prerequisite: true,
        })
        if (!active) return
        const source = dataSources.find(
          ds => ds.data_source_instance?.trim().toLowerCase() === instance
        )
        const schemaDefinition = (source?.schema_definition ?? {}) as DynamicSchemaDefinition
        setPrerequisiteData(source?.prerequisite ?? null)
        const fields = (schemaDefinition.destination ?? [])
          .filter(field => field?.show !== false && !!field?.key)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        setDynamicConfigFields(fields)
        setForm(prev => {
          if (registeredDestination) {
            // Avoid wrong defaults (e.g. port) from the generic destination schema; registered
            // rows supply connection fields via the list + registered-tables APIs.
            return prev
          }
          const next = { ...prev }
          fields.forEach(field => {
            const key = field.key ?? ''
            if (!key) return
            if (next[key]?.trim()) return
            if (field.defaultValue != null && String(field.defaultValue).trim()) {
              next[key] = String(field.defaultValue)
            }
          })
          return next
        })
      } catch {
        if (!active) return
        setDynamicConfigFields([])
        setPrerequisiteData(null)
      } finally {
        if (!active) return
        setIsDynamicConfigLoading(false)
      }
    }
    void loadSchema()
    return () => {
      active = false
    }
  }, [registeredDestination, allowedInstance])

  const connectionPayload = useMemo(() => {
    const payload: Record<string, unknown> = { type: normalizedType }
    Object.entries(form).forEach(([key, value]) => {
      const v = value?.trim()
      if (!v) return
      if (key === 'port') {
        const port = Number(v)
        payload[key] = Number.isFinite(port) ? port : v
      } else if (key === 'destination_name') {
        payload.source_name = v
      } else {
        payload[key] = v
      }
    })
    if (!payload.source_name) {
      payload.source_name = destinationName.trim() || 'test_destination'
    }
    return payload
  }, [destinationName, form, normalizedType])

  const schemaTestFields = useMemo(
    () =>
      testResponseFields.filter(field => {
        if (field.show === false) return false
        const type = (field.type ?? '').trim().toLowerCase()
        return type === 'matselect' || type === 'select'
      }),
    [testResponseFields]
  )
  const hasRequiredTestSelections = useMemo(
    () =>
      schemaTestFields.every(field => {
        const selectedValue = (responseSelections[field.key] ?? '').trim()
        const isRequired = field.validators?.includes('required') ?? false
        return isRequired ? Boolean(selectedValue) : true
      }),
    [responseSelections, schemaTestFields]
  )
  const outputPreviewColumnOptions = useMemo(
    () => outputPreviewRows.map(row => row.fieldName).filter(Boolean),
    [outputPreviewRows]
  )
  const toPayloadValue = useCallback((key: string, raw: string): unknown => {
    const value = raw.trim()
    if (!value) return value
    if (key === 'port') {
      const n = Number(value)
      return Number.isFinite(n) ? n : value
    }
    if (key === 'ssl_mode') {
      const lower = value.toLowerCase()
      if (lower === 'true') return true
      if (lower === 'false') return false
    }
    if (['api_headers', 'api_body', 'api_params'].includes(key)) {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    }
    return value
  }, [])
  const buildDestinationPayload = useCallback(() => {
    const payload: Record<string, unknown> = { type: normalizedType }
    Object.entries(form).forEach(([key, rawValue]) => {
      const v = rawValue?.trim()
      if (!v) return
      payload[key] = toPayloadValue(key, rawValue)
    })
    Object.entries(responseSelections).forEach(([key, rawValue]) => {
      const v = rawValue?.trim()
      if (!v) return
      payload[key] = toPayloadValue(key, rawValue)
    })
    if (payload.destination_name && !payload.source_name) {
      payload.source_name = payload.destination_name
    }
    const fallbackName = destinationName.trim() || 'test_destination'
    if (!payload.source_name) payload.source_name = fallbackName
    const rawTableName = String(
      payload.table_name ||
      payload.file_name ||
      payload.destination_name ||
      payload.source_name ||
      'target_table'
    )
    payload.table_name = normalizeTableName(rawTableName)

    // Backend schema for `POST /target-data-sources` is strict and rejects these fields.
    // Keep `source_name` but remove destination/metadata-specific keys.
    const forbiddenKeys = [
      'destination_name',
      'data_source_type',
      'data_source_instance_label',
      'image_link',
      'target_data_source_id',
    ] as const
    for (const key of forbiddenKeys) {
      if (key in payload) delete payload[key]
    }
    return payload
  }, [destinationName, form, normalizedType, registeredDestination, responseSelections, toPayloadValue])

  const buildTargetDataSourcePayload = useCallback(() => {
    const payload = buildDestinationPayload()
    const sanitizedPayload = { ...payload }

    if (!sanitizedPayload.source_name && sanitizedPayload.destination_name) {
      sanitizedPayload.source_name = sanitizedPayload.destination_name
    }

    delete sanitizedPayload.destination_name
    delete sanitizedPayload.data_source_type
    delete sanitizedPayload.data_source_instance_label
    delete sanitizedPayload.image_link
    delete sanitizedPayload.target_data_source_id

    return sanitizedPayload as Record<string, unknown> & { type: string; source_name: string }
  }, [buildDestinationPayload])

  const buildPhysicalTablesDatabasePayload = useCallback(() => {
    const payload = buildTargetDataSourcePayload()
    const databasePayload: Record<string, unknown> = {}
    const allowedKeys = [
      'type',
      'source_name',
      'table_name',
      'host',
      'port',
      'database',
      'username',
      'password',
      'schema',
    ] as const

    allowedKeys.forEach(key => {
      const value = payload[key]
      if (value === undefined || value === null) return
      if (typeof value === 'string' && !value.trim()) return
      databasePayload[key] = value
    })

    if (!databasePayload.type) {
      databasePayload.type = normalizedType
    }
    if (!databasePayload.source_name) {
      databasePayload.source_name = String(
        payload.source_name ?? destinationName ?? 'test_destination'
      )
    }
    if (!databasePayload.table_name) {
      databasePayload.table_name = String(payload.table_name ?? 'target_table')
    }


    if (isRegisteredDestination && registeredDestination) {
      // Prefer `getRegisteredTables` + list API + parsed endpoint over dynamic-schema form defaults
      // (defaults often ship a wrong port such as 5432).
      const { host: endpointHost, port: endpointPort } = extractHostPortFromEndpoint(
        registeredDestination.endpoint ?? ''
      )
      const endpointSchema = extractSchemaNameFromEndpoint(registeredDestination.endpoint ?? '')
      const details = registeredDestinationConnectionDetails
      const fromList = buildConnectionDetailsFromRegisteredListItem({
        databaseName: registeredDestination.databaseName,
        username: registeredDestination.username,
        password: registeredDestination.password,
        endpoint: registeredDestination.endpoint ?? '',
      })

      const host = firstNonEmptyTrimmed(
        details?.host,
        fromList.host,
        endpointHost
      )
      if (host) databasePayload.host = host

      const portResolved = normalizePortForDatabasePayload(
        firstNonEmptyTrimmed(
          normalizePortString(details?.port),
          fromList.port,
          normalizePortString(endpointPort)
        )
      )
      if (portResolved !== undefined) databasePayload.port = portResolved

      const database = firstNonEmptyTrimmed(
        details?.database,
        fromList.database,
        registeredDestination.databaseName
      )
      if (database) databasePayload.database = database

      const username = firstNonEmptyTrimmed(
        details?.username,
        fromList.username,
        registeredDestination.username
      )
      if (username) databasePayload.username = username

      const password = firstNonEmptyTrimmed(
        details?.password,
        fromList.password,
        registeredDestination.password
      )
      if (password) databasePayload.password = password

      if (registeredInstanceSupportsSchema) {
        const schema = firstUsableConnectionString(
          details?.schema,
          fromList.schema,
          endpointSchema
        )
        if (schema) databasePayload.schema = schema
        else delete databasePayload.schema
      } else {
        delete databasePayload.schema
      }
    }

    return databasePayload as Record<string, unknown> & { type: string; source_name: string }
  }, [
    buildTargetDataSourcePayload,
    destinationName,
    isRegisteredDestination,
    normalizedType,
    registeredInstanceSupportsSchema,
    registeredDestinationConnectionDetails,
    registeredDestination,
  ])

  // ─── Form validation helper ─────────────────────────────────────────────────
  const validateForm = useCallback((): boolean => {
    if (isRegisteredDestination) {
      setValidationErrors({});
      return true;
    }
    if (dynamicConfigFields.length === 0) {
      setValidationErrors({});
      return false;
    }
    const fields = dynamicConfigFields
    const resolvedDestinationName = firstNonEmptyTrimmed(
      form.destination_name,
      form.source_name,
      destinationName
    )
    const formForValidation = { ...form }
    if (resolvedDestinationName) {
      if (!formForValidation.destination_name?.trim()) {
        formForValidation.destination_name = resolvedDestinationName
      }
      if (!formForValidation.source_name?.trim()) {
        formForValidation.source_name = resolvedDestinationName
      }
    }
    const errors = validateFormFields(formForValidation, fields)
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [destinationName, form, dynamicConfigFields, isRegisteredDestination])

  const handleTest = useCallback(async () => {
    if (!validateForm()) return

    setIsTesting(true)
    try {
      const fields = await testAdpConnection([
        connectionPayload as AdpConnectionTestDatabasePayload,
      ])
      setTestResponseFields(fields)
      setResponseSelections(
        fields.reduce<Record<string, string>>((acc, field) => {
          acc[field.key] = field.defaultValue ?? ''
          return acc
        }, {})
      )
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to test destination connection'
      setTestResponseFields([])
      setResponseSelections({})
      toast.error(errorMsg)
    } finally {
      setIsTesting(false)
    }
  }, [connectionPayload, validateForm])

  const handleNext = useCallback(async () => {
    if (!validateForm()) return

    setIsSubmittingTarget(true)
    try {
      const payload = buildTargetDataSourcePayload()

      await createAdpTargetDataSource({
        databases: [payload],
        ref_type: 'ML',
      })
      setDestinationDetailsAccordionValue('')
      setLoadTypeOpen(false)
      setOutputPreviewOpen(isNonDbDestination)
      setColumnMappingOpen(false)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create target data source'
      toast.error(errorMsg)
    } finally {
      setIsSubmittingTarget(false)
    }
  }, [buildTargetDataSourcePayload, isNonDbDestination, validateForm])

  const sessionSnapshot = useAdpPipelineSessionSnapshot()
  const persistedColumnMappingSignature = useMemo(
    () => JSON.stringify(sessionSnapshot.columnMapping ?? []),
    [sessionSnapshot.columnMapping]
  )
  const persistedFullOperationSignature = useMemo(
    () => JSON.stringify(sessionSnapshot.fullOperationJson ?? {}),
    [sessionSnapshot.fullOperationJson]
  )

  const mappingResult = useMemo(() => {
    if (!configuringNodeId)
      return {
        rows: [] as ColumnMappingRow[],
        fullOperationJson: {} as Record<string, unknown>,
      }
    const emptyResult = {
      rows: [] as ColumnMappingRow[],
      fullOperationJson: {} as Record<string, unknown>,
    }

    const session = sessionSnapshot
    const incomingByTarget = new Map<string, string[]>()
    for (const edge of graphSnapshot.edges) {
      const arr = incomingByTarget.get(edge.target) ?? []
      arr.push(edge.source)
      incomingByTarget.set(edge.target, arr)
    }

    const nodeById = new Map(graphSnapshot.nodes.map(n => [n.id, n]))

    const walkBackPath = (startId: string): string[] => {
      const out: string[] = []
      let current = startId
      const seen = new Set<string>()
      while (!seen.has(current)) {
        seen.add(current)
        const parents = incomingByTarget.get(current) ?? []
        if (parents.length === 0) break
        const parent = parents[0]
        out.unshift(parent)
        current = parent
      }
      return out
    }

    const collectUpstreamSourceNodeIds = (startId: string): string[] => {
      const out = new Set<string>()
      const stack = [startId]
      const seen = new Set<string>()
      while (stack.length > 0) {
        const current = stack.pop()!
        if (seen.has(current)) continue
        seen.add(current)
        const parents = incomingByTarget.get(current) ?? []
        for (const p of parents) {
          const node = nodeById.get(p)
          const groupId = (node?.data as { groupId?: string } | undefined)?.groupId
          if (groupId === 'sources') out.add(p)
          stack.push(p)
        }
      }
      return [...out]
    }

    const applyTransformationOnColumns = (
      cols: AdpAssetColumnEntry[],
      nodeId: string
    ): AdpAssetColumnEntry[] => {
      const saved = session.transformations[nodeId]
      if (!saved?.form) return cols
      const form = saved.form as Record<string, unknown>
      let renamed = [...cols]

      const equalsColumnName = (a: string, b: string) =>
        a.trim().toLowerCase() === b.trim().toLowerCase()

      const addCol = (name: string, dataType = 'string') => {
        if (!renamed.find(c => equalsColumnName(c.name, name))) {
          renamed.push({ name, dataType, isBusinessPrimaryKey: false })
        }
      }

      const dropCol = (name: string) => {
        renamed = renamed.filter(c => !equalsColumnName(c.name, name))
      }

      // --- 1. Rename Columns ---
      const renamePayload = form.renameColumnsOperationJson as
        | Record<string, { renameColumnsList?: [string, string][] }>
        | undefined
      const renamePairs: Array<[string, string]> = []
      if (renamePayload) {
        for (const row of Object.values(renamePayload)) {
          const pairs = row?.renameColumnsList ?? []
          pairs.forEach(pair => renamePairs.push(pair))
        }
      }

      const applied = form.__appliedTransformations
      if (Array.isArray(applied)) {
        for (const item of applied) {
          if (!item || typeof item !== 'object') continue
          const values = (item as { values?: Record<string, unknown> }).values
          if (!values || typeof values !== 'object') continue
          const oldName = String(values.oldColumn ?? values.oldColumnName ?? '').trim()
          const newName = String(values.newColumn ?? values.newColumnName ?? '').trim()
          if (oldName && newName) renamePairs.push([oldName, newName])
        }
      }

      for (const [from, to] of renamePairs) {
        const target = from?.trim()
        const next = to?.trim()
        if (!target || !next) continue
        const idx = renamed.findIndex(c => equalsColumnName(c.name, target))
        if (idx >= 0) {
          renamed[idx] = { ...renamed[idx], name: next }
        }
      }

      // --- 2. Data Type Conversion ---
      const dataTypeConversionPayload = form.dataTypeConversionOperationJson as
        | Record<string, { dataTypeConversion_zip?: Array<[string, string]> }>
        | undefined
      const conversionPairs: Array<[string, string]> = []
      if (dataTypeConversionPayload) {
        for (const row of Object.values(dataTypeConversionPayload)) {
          const pairs = row?.dataTypeConversion_zip ?? []
          pairs.forEach(pair => {
            if (!Array.isArray(pair) || pair.length < 2) return
            const col = String(pair[0] ?? '').trim()
            const nextType = String(pair[1] ?? '').trim()
            if (col && nextType) conversionPairs.push([col, nextType])
          })
        }
      }
      for (const [colName, nextTypeRaw] of conversionPairs) {
        const idx = renamed.findIndex(c => equalsColumnName(c.name, colName))
        if (idx >= 0) {
          renamed[idx] = { ...renamed[idx], dataType: nextTypeRaw }
        }
      }

      // --- 3. Date to Integer ---
      try {
        const diPayload = form.dateToIntegerOperationJson as Record<string, string> | undefined
        if (diPayload?.DI01) {
          const rows = JSON.parse(diPayload.DI01) as any[]
          rows.forEach(r => {
            if (r.NewDateKeyColumnName) addCol(r.NewDateKeyColumnName, r.KeyType || 'int')
          })
        }
      } catch (e) { }

      // --- 4. Concat ---
      try {
        const ccPayload = form.concatOperationJson as Record<string, string> | undefined
        if (ccPayload?.CC01) {
          const rows = JSON.parse(ccPayload.CC01) as any[]
          rows.forEach(r => {
            if (r.New_ColumnName) addCol(r.New_ColumnName, 'string')
          })
        }
      } catch (e) { }

      // --- 5. Cumulative ---
      try {
        const cuPayload = form.cumulativeOperationJson as Record<string, string> | undefined
        if (cuPayload?.CU01) {
          const rows = JSON.parse(cuPayload.CU01) as any[]
          rows.forEach(r => {
            if (r.NewColumnName) addCol(r.NewColumnName, 'float')
          })
        }
      } catch (e) { }

      // --- 6. Derived Column ---
      try {
        const dePayload = form.derivedColumnOperationJson as Record<string, string> | undefined
        if (dePayload?.DE01) {
          const rows = JSON.parse(dePayload.DE01) as any[]
          rows.forEach(r => {
            if (r.DerivedColumnCondition) {
              const match = String(r.DerivedColumnCondition).match(/\bAS\s+([a-zA-Z0-9_]+)\b/i)
              if (match && match[1]) addCol(match[1], 'string')
            }
          })
        }
      } catch (e) { }

      // --- 7. Drop Fields ---
      try {
        const dfPayload = form.dropFieldsOperationJson as Record<string, string> | undefined
        if (dfPayload?.DF01) {
          const obj = JSON.parse(dfPayload.DF01) as any
          if (Array.isArray(obj.dropFieldsList)) {
            obj.dropFieldsList.forEach((col: string) => dropCol(col))
          }
        }
      } catch (e) { }

      // --- 8. Lookup ---
      try {
        const lkPayload = (form.lookupOpeartionJson || form.lookupOperationJson) as
          | Record<string, string>
          | undefined
        if (lkPayload?.LK01) {
          const rows = JSON.parse(lkPayload.LK01) as any[]
          rows.forEach(r => {
            if (typeof r.LookUpGetColumns === 'string') {
              r.LookUpGetColumns.split(',').forEach((c: string) => {
                const col = c.trim()
                if (col) addCol(col, 'string')
              })
            }
          })
        }
      } catch (e) { }

      // --- 9. Aggregate ---
      try {
        const agPayload = (form.aggregationOpeartionJson || form.aggregationOperationJson) as any
        if (agPayload?.AG01) {
          const list = agPayload.AG01.aggregateList
          if (Array.isArray(list)) {
            const groupCols = new Set<string>()
            const newCols = new Set<string>()
            list.forEach((item: any[]) => {
              if (Array.isArray(item[1])) {
                item[1].forEach((g: string) => groupCols.add(g))
              }
              if (item[3]) {
                newCols.add(item[3])
              }
            })
            if (groupCols.size > 0 || newCols.size > 0) {
              renamed = renamed.filter(c => groupCols.has(c.name))
              newCols.forEach(c => addCol(c, 'float'))
            }
          }
        }
      } catch (e) { }

      // --- 10. Transpose ---
      try {
        const tpPayload = form.transposeOperationJson as Record<string, string> | undefined
        if (tpPayload?.TP01) {
          const obj = JSON.parse(tpPayload.TP01) as any
          const fixed = Array.isArray(obj.fixed) ? obj.fixed : []
          const transposeHeaders = Array.isArray(obj.transposeHeaders) ? obj.transposeHeaders : []

          renamed = renamed.filter(c => fixed.includes(c.name))
          transposeHeaders.forEach((h: any) => {
            if (h.name) addCol(h.name, h.dataType || 'string')
          })
        }
      } catch (e) { }

      // --- 11. Split ---
      try {
        const spPayload = form.splitOperationJson as Record<string, string> | undefined
        if (spPayload?.SP01) {
          const rows = JSON.parse(spPayload.SP01) as any[]
          rows.forEach(r => {
            if (typeof r.NewColumns === 'string') {
              r.NewColumns.split(',').forEach((c: string) => {
                const col = c.trim()
                if (col) addCol(col, 'string')
              })
            }
          })
        }
      } catch (e) { }

      // --- 12. Date Split ---
      try {
        const dsPayload = form.dateSplitOperationJson as Record<string, string> | undefined
        if (dsPayload?.DS01) {
          const obj = JSON.parse(dsPayload.DS01) as any
          if (obj.DateColumn) {
            addCol(`${obj.DateColumn}_year`, 'int')
            addCol(`${obj.DateColumn}_month`, 'int')
            addCol(`${obj.DateColumn}_day`, 'int')
          }
        }
      } catch (e) { }

      // --- 13. Z-Score ---
      try {
        const zsPayload = form.zscoreOperationJson as Record<string, string> | undefined
        if (zsPayload?.ZS01) {
          const rows = JSON.parse(zsPayload.ZS01) as any[]
          rows.forEach(r => {
            if (r.new_column) addCol(r.new_column, 'float')
          })
        }
      } catch (e) { }

      // --- 14. Box Cox ---
      try {
        const bcPayload = form.boxcoxOperationJson as Record<string, string> | undefined
        if (bcPayload?.BC01) {
          const rows = JSON.parse(bcPayload.BC01) as any[]
          rows.forEach(r => {
            if (r.new_column_name) addCol(r.new_column_name, 'float')
          })
        }
      } catch (e) { }

      // --- 15. Sort Operations (Dynamic API-driven + Legacy) ---
      try {
        // Try dynamic API-driven format first
        const srPayload = form.srOperationJson as Record<string, Record<string, unknown>> | undefined
        if (srPayload?.SR01) {
          const sortData = srPayload.SR01
          const sortColumns = Array.isArray(sortData.sort_columns)
            ? sortData.sort_columns
            : typeof sortData.sort_columns === 'string'
              ? sortData.sort_columns.split(',').map(c => c.trim()).filter(Boolean)
              : []

          if (sortColumns.length > 0) {
            // Sort doesn't change columns, just reorder them
            // Keep the same columns but mark them as sorted
            renamed = renamed.map(col => ({
              ...col,
              // You could add metadata here if needed
            }))
          }
        } else {
          // Fallback to legacy sort format
          const legacySortPayload = form.sortOperationJson as Record<string, Record<string, unknown>> | undefined
          if (legacySortPayload?.SR01) {
            const sortData = legacySortPayload.SR01
            const sortColumns = Array.isArray(sortData.sort_columns)
              ? sortData.sort_columns
              : typeof sortData.sort_columns === 'string'
                ? sortData.sort_columns.split(',').map(c => c.trim()).filter(Boolean)
                : []

            if (sortColumns.length > 0) {
              // Sort doesn't change columns, just reorder them
              renamed = renamed.map(col => ({ ...col }))
            }
          }
        }
      } catch (e) {
        console.warn('Error processing sort operations:', e)
      }

      // --- 16. Join Operations ---
      try {
        const joinPayload = form.joinOperationJson as Record<string, string> | undefined
        if (joinPayload) {
          // Get join operations sorted by key (JR01, JR02, etc.)
          const joinKeys = Object.keys(joinPayload).sort()

          for (const joinKey of joinKeys) {
            const joinRowsJson = joinPayload[joinKey]
            if (!joinRowsJson) continue

            const joinRows = JSON.parse(joinRowsJson) as any[]
            const joinRow = joinRows[0]
            if (!joinRow) continue

            const joinType = (joinRow.JoinType || 'left').toLowerCase()
            const sourceGetColumns = joinRow.SourceTableGetColumns || ''
            const joinGetColumns = joinRow.JoinTableGetColumns || ''
            const sourceKey = joinRow.SourceTableKey || ''
            const joinTableKey = joinRow.JoinTableKey || ''

            // Parse column lists
            const sourceColumns = sourceGetColumns.split(',').map(c => c.trim()).filter(Boolean)
            const joinColumns = joinGetColumns.split(',').map(c => c.trim()).filter(Boolean)

            // Apply join logic based on join type
            const resultColumnNames = new Set<string>()

            if (joinType === 'inner') {
              // Inner join: only columns from both sides (intersection logic)
              sourceColumns.forEach(col => resultColumnNames.add(col))
              joinColumns.forEach(col => {
                if (col !== joinTableKey && !resultColumnNames.has(col)) {
                  resultColumnNames.add(col)
                }
              })
            } else if (joinType === 'left') {
              // Left join: all from source + non-key from join side  
              sourceColumns.forEach(col => resultColumnNames.add(col))
              joinColumns.forEach(col => {
                if (col !== joinTableKey && !resultColumnNames.has(col)) {
                  resultColumnNames.add(col)
                }
              })
            } else if (joinType === 'right') {
              // Right join: all from join side + non-key from source
              joinColumns.forEach(col => resultColumnNames.add(col))
              sourceColumns.forEach(col => {
                if (col !== sourceKey && !resultColumnNames.has(col)) {
                  resultColumnNames.add(col)
                }
              })
            } else if (joinType === 'outer' || joinType === 'full') {
              // Full outer join: all columns from both sides
              sourceColumns.forEach(col => resultColumnNames.add(col))
              joinColumns.forEach(col => {
                if (!resultColumnNames.has(col)) {
                  resultColumnNames.add(col)
                }
              })
            }

            // Update renamed columns based on join result
            const finalColumns: typeof renamed = []
            for (const colName of resultColumnNames) {
              const existingCol = renamed.find(c => equalsColumnName(c.name, colName))
              if (existingCol) {
                finalColumns.push(existingCol)
              } else {
                // Add new column from join side
                finalColumns.push({
                  name: colName,
                  dataType: 'string',
                  isBusinessPrimaryKey: colName === sourceKey || colName === joinTableKey,
                })
              }
            }
            renamed = finalColumns
          }
        }
      } catch (e) {
        console.warn('Error processing join operations:', e)
      }

      return renamed
    }

    const sourceNodeIds = collectUpstreamSourceNodeIds(configuringNodeId)
    const firstSourceAsset = sourceNodeIds
      .map(id => session.assets[id])
      .find(a => a?.columns?.length)

    const sourceColumns = firstSourceAsset?.columns ?? []
    const sourceName = firstSourceAsset?.dataSourceName ?? 'Source'

    const pathToDestination = walkBackPath(configuringNodeId)
    const orderedOperationJson: Record<string, unknown> = {}
    const collectOperationJson = (form: Record<string, unknown> | undefined) => {
      if (!form) return
      Object.entries(form).forEach(([key, value]) => {
        if (!/(?:OperationJson|OpeartionJson)$/.test(key)) return
        if (!value || typeof value !== 'object' || Array.isArray(value)) return
        Object.entries(value as Record<string, unknown>).forEach(([opKey, opValue]) => {
          orderedOperationJson[opKey] = opValue
        })
      })
    }

    let resultColumns = sourceColumns.map(c => ({ ...c }))
    for (const nodeId of pathToDestination) {
      collectOperationJson(session.transformations[nodeId]?.form)
      collectOperationJson(session.integrations[nodeId]?.form)
      if (session.assets[nodeId]?.columns?.length) {
        resultColumns = session.assets[nodeId].columns.map(c => ({ ...c }))
        continue
      }
      resultColumns = applyTransformationOnColumns(resultColumns, nodeId)
    }

    // Check if this path involves join operations by looking for integration nodes in the path
    const hasJoinInPath = pathToDestination.some(nodeId => {
      const nodeData = nodeById.get(nodeId)?.data as { groupId?: string; connectorId?: string } | undefined
      return nodeData?.groupId === 'integrations' && nodeData?.connectorId === 'join'
    })

    let rows: ColumnMappingRow[]

    if (hasJoinInPath && resultColumns.length > 0) {
      // For join scenarios: use name-based mapping from resultColumns to themselves
      // This ensures the join output columns are properly mapped as identity mappings
      rows = resultColumns.map(dst => {
        // Try to find matching source column by name from any upstream source
        let matchingSourceCol: AdpAssetColumnEntry | undefined
        let matchingSourceName = sourceName

        // Search all upstream source assets for matching column name
        for (const sourceNodeId of sourceNodeIds) {
          const sourceAsset = session.assets[sourceNodeId]
          if (sourceAsset?.columns?.length) {
            const matchedCol = sourceAsset.columns.find(col =>
              col.name.toLowerCase().trim() === dst.name.toLowerCase().trim()
            )
            if (matchedCol) {
              matchingSourceCol = matchedCol
              matchingSourceName = sourceAsset.dataSourceName ?? sourceName
              break
            }
          }
        }

        return {
          sourceName: matchingSourceName,
          sourceColumn: matchingSourceCol?.name ?? dst.name, // Use destination name as fallback
          sourceDataType: matchingSourceCol?.dataType ?? dst.dataType,
          destinationColumn: dst.name,
          destinationDataType: dst.dataType,
          destinationIsBusinessKey: Boolean(dst.isBusinessPrimaryKey ?? matchingSourceCol?.isBusinessPrimaryKey),
        }
      })
    } else {
      // Legacy logic: index-based mapping for non-join scenarios
      const rowCount = Math.max(sourceColumns.length, resultColumns.length)
      rows = Array.from({ length: rowCount }, (_, idx) => {
        const src = sourceColumns[idx] as AdpAssetColumnEntry | undefined
        const dst = resultColumns[idx] as AdpAssetColumnEntry | undefined
        return {
          sourceName,
          sourceColumn: src?.name ?? '',
          sourceDataType: src?.dataType ?? '',
          destinationColumn: dst?.name ?? '',
          destinationDataType: dst?.dataType ?? '',
          destinationIsBusinessKey: Boolean(dst?.isBusinessPrimaryKey ?? src?.isBusinessPrimaryKey),
        }
      }).filter(r => r.sourceColumn || r.destinationColumn)
    }
    // Filter out empty rows only for non-join scenarios
    const filteredRows = hasJoinInPath ? rows : rows.filter(r => r.sourceColumn || r.destinationColumn)
    if (filteredRows.length === 0) return emptyResult
    const columnMapping: Array<[string, string, string, string]> = filteredRows.map(row => [
      row.sourceColumn || '',
      row.sourceDataType || '',
      row.destinationColumn || '',
      row.destinationDataType || '',
    ])

    orderedOperationJson.columnMapping = columnMapping
    return { rows: filteredRows, fullOperationJson: orderedOperationJson }
  }, [configuringNodeId, graphSnapshot.edges, graphSnapshot.nodes, sessionSnapshot])

  const columnMappingRows = mappingResult.rows
  const destinationColumnOptions = useMemo(() => {
    const seen = new Set<string>()
    const options: string[] = []

    for (const row of outputPreviewRows) {
      const col = row.fieldName?.trim()
      if (!col || seen.has(col)) continue
      seen.add(col)
      options.push(col)
    }

    // Fallback to existing mapped destination columns if output preview isn't populated yet.
    for (const row of columnMappingRows) {
      const col = row.destinationColumn?.trim()
      if (!col || seen.has(col)) continue
      seen.add(col)
      options.push(col)
    }

    return options
  }, [columnMappingRows, outputPreviewRows])

  const derivedOutputPreviewRows = useMemo<OutputPreviewRow[]>(() => {
    return columnMappingRows.map((row, index) => {
      const fieldName = row.destinationColumn || row.sourceColumn || `column_${index + 1}`
      const rawType = row.destinationDataType || row.sourceDataType || 'string'
      const dataType = normalizeOutputDataType(rawType)
      return {
        id: `${fieldName}::${index}`,
        fieldName,
        fieldDataType: dataType,
        fieldLength: defaultFieldLengthForDataType(rawType),
        isSelected: true,
        isPrimaryKey: row.destinationIsBusinessKey,
      }
    })
  }, [columnMappingRows])

  useEffect(() => {
    setOutputPreviewRows(prev => {
      const prevById = new Map(prev.map(row => [row.id, row]))
      return derivedOutputPreviewRows.map(row => {
        const existing = prevById.get(row.id)
        if (!existing) return row
        const nextType = normalizeOutputDataType(existing.fieldDataType || row.fieldDataType)
        return {
          ...row,
          isSelected: existing.isSelected,
          isPrimaryKey: existing.isPrimaryKey,
          fieldDataType: nextType,
          fieldLength: nextType === 'string' ? existing.fieldLength || row.fieldLength || 1024 : 0,
        }
      })
    })
  }, [derivedOutputPreviewRows])

  useEffect(() => {
    if (columnMappingRows.length === 0) return
    const columnMapping: Array<[string, string, string, string]> = columnMappingRows.map(row => [
      row.sourceColumn || '',
      row.sourceDataType || '',
      row.destinationColumn || '',
      row.destinationDataType || '',
    ])
    const nextColumnMappingSignature = JSON.stringify(columnMapping)
    const nextFullOperationSignature = JSON.stringify(mappingResult.fullOperationJson ?? {})

    if (nextColumnMappingSignature !== persistedColumnMappingSignature) {
      upsertColumnMappingSession(columnMapping)
    }
    if (nextFullOperationSignature !== persistedFullOperationSignature) {
      upsertFullOperationJsonSession(mappingResult.fullOperationJson)
    }
  }, [
    columnMappingRows,
    mappingResult.fullOperationJson,
    persistedColumnMappingSignature,
    persistedFullOperationSignature,
  ])

  const persistPipelineDefinition = useCallback(async (): Promise<boolean> => {
    if (!onSavePipelineDefinition) return false
    setIsSaving(true)
    try {
      await onSavePipelineDefinition()
      return true
    } finally {
      setIsSaving(false)
    }
  }, [onSavePipelineDefinition])

  const handleLoadTypeSave = useCallback(() => {
    setLoadTypeOpen(false)
    setOutputPreviewOpen(true)
    setColumnMappingOpen(false)
  }, [])
  const handleColumnMappingSave = useCallback(async () => {
    try {
      const didSave = await persistPipelineDefinition()
      if (!didSave) return
      if (!configuringNodeId || !dataFlowId) {
        onColumnMappingSaved?.()
        return
      }
      const session = readAdpPipelineSession()
      const incomingByTarget = new Map<string, string[]>()
      for (const edge of graphSnapshot.edges) {
        const arr = incomingByTarget.get(edge.target) ?? []
        arr.push(edge.source)
        incomingByTarget.set(edge.target, arr)
      }
      const nodeById = new Map(graphSnapshot.nodes.map(n => [n.id, n]))
      const sourceNodeIds = new Set<string>()
      const stack = [configuringNodeId]
      const seen = new Set<string>()
      while (stack.length > 0) {
        const current = stack.pop()!
        if (seen.has(current)) continue
        seen.add(current)
        const parents = incomingByTarget.get(current) ?? []
        for (const parent of parents) {
          const groupId = (nodeById.get(parent)?.data as { groupId?: string } | undefined)?.groupId
          if (groupId === 'sources') sourceNodeIds.add(parent)
          stack.push(parent)
        }
      }
      const sourceAssets = [...sourceNodeIds].map(id => session.assets[id]).filter(Boolean)
      const destinationAsset = session.assets[configuringNodeId]

      const payload = {
        roo_id: toUniquePositiveIds(sourceAssets.map(asset => asset.rooId)),
        source_file_id: toUniquePositiveIds(sourceAssets.map(asset => asset.sourceFileId)),
        source_data_source_id: toUniquePositiveIds(
          sourceAssets.map(asset => asset.sourceDataSourceId)
        ),
        target_data_source_id: toUniquePositiveIds([destinationAsset?.targetDataSourceId]),
        target_table_id: toUniquePositiveIds([destinationAsset?.targetTableId]),
      }

      if (
        payload.roo_id.length === 0 ||
        payload.source_file_id.length === 0 ||
        payload.source_data_source_id.length === 0 ||
        payload.target_data_source_id.length === 0 ||
        payload.target_table_id.length === 0
      ) {
        const missing: string[] = []
        if (payload.roo_id.length === 0) missing.push('roo_id')
        if (payload.source_file_id.length === 0) missing.push('source_file_id')
        if (payload.source_data_source_id.length === 0) missing.push('source_data_source_id')
        if (payload.target_data_source_id.length === 0) missing.push('target_data_source_id')
        if (payload.target_table_id.length === 0) missing.push('target_table_id')
        throw new Error(
          `Missing source/destination IDs (${missing.join(', ')}). Re-save source and destination details before column mapping.`
        )
      }

      await postAdpDataFlowMapping(dataFlowId, payload)
      onColumnMappingSaved?.()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save column mapping.'
      toast.error(errorMsg)
    }
  }, [
    configuringNodeId,
    dataFlowId,
    graphSnapshot.edges,
    graphSnapshot.nodes,
    onColumnMappingSaved,
    persistPipelineDefinition,
    toast,
  ])

  const handleOutputPreviewSave = useCallback(async () => {
    const selectedRows =
      effectivePhysicalTableChoice === 'yes'
        ? outputPreviewRows.filter(row => row.isSelected)
        : outputPreviewRows
    if (selectedRows.length === 0) {
      toast.error('No output columns available to persist for target table creation.')
      return
    }
    setIsPublishingTarget(true)
    try {
      if (isRegisteredDestination && registeredTableSelectionMode === 'existing') {
        if (!registeredExistingTableIds) {
          toast.error(
            'Missing target table identifiers. Open Destination Details and save your table selection again.'
          )
          return
        }
        const payload = buildDestinationPayload()
        const tableName = getSafeString(payload.table_name) || 'target_table'
        if (configuringNodeId) {
          const sessionPayload = {
            nodeId: configuringNodeId,
            assetKind: 'destination' as const,
            dataSourceName: String(payload.source_name ?? destinationName ?? 'target_data_source'),
            dataSourceInstance: String(payload.type ?? normalizedType),
            targetDataSourceId: registeredExistingTableIds.targetDataSourceId,
            targetTableId: registeredExistingTableIds.targetTableId,
            schemaName:
              typeof payload.schema === 'string' && payload.schema.trim()
                ? payload.schema
                : undefined,
            tableName,
            columns: selectedRows.map(row => ({
              name: row.fieldName,
              dataType: row.fieldDataType,
              selected: true,
              isBusinessPrimaryKey: row.isPrimaryKey,
            })),
          }
          upsertAssetSession(sessionPayload)
          if (graphSnapshot.edges.length > 0 && graphSnapshot.nodes.length > 0) {
            syncUpstreamSourcePrimaryKeyFromDestinationPreview({
              destinationNodeId: configuringNodeId,
              destinationFields: selectedRows.map(row => ({
                fieldName: row.fieldName,
                isPrimaryKey: row.isPrimaryKey,
              })),
              graphEdges: graphSnapshot.edges,
              graphNodes: graphSnapshot.nodes,
            })
          }
        }
        setOutputPreviewOpen(false)
        setColumnMappingOpen(true)
        return
      }

      const payload = buildDestinationPayload();
      const tableName = getSafeString(payload.table_name) || 'target_table';
      const category = resolveDestinationCategory(
        String(registeredDestination?.dataSourceType ?? payload.data_source_type ?? ''),
        String(payload.type ?? normalizedType)
      )
      const loadTypeCode: 1 | 2 = isNonDbDestination ? 1 : (loadMode === 'full' ? 1 : 2)
      const scdTypeCode: 1 | 2 = scdType === 'scd_1' ? 1 : 2

      const targetTablesPayload: AdpTargetTablesBatchBody = {
        target_table: [
          {
            data_source_name: resolveDataSourceName(
              payload.source_name,
              destinationName
            ),
            table_name: tableName,
            target_table_type: getTargetTableType(category),
            path: buildTargetPath(category, payload),
            glue_table_name: resolveGlueTableName(payload.glue_table_name),
            load_type_code: loadTypeCode,
            scd_type_code: scdTypeCode,
            ref_type: 'ML',
          },
        ],
        target_table_column: selectedRows.map((row) => ({
          table_name: tableName,
          column_name: row.fieldName,
          column_data_type: row.fieldDataType,
          column_length: String(row.fieldLength ?? 0),
          precision: 0,
          is_business_primary_key: row.isPrimaryKey,
        })),
      };

      if (effectivePhysicalTableChoice === 'yes') {
        const physicalTablesDatabasePayload = buildPhysicalTablesDatabasePayload()
        const physicalTablesPayload: AdpPhysicalTablesBody = {
          databases: [physicalTablesDatabasePayload],
          columns: selectedRows.map(row => ({
            column_name: row.fieldName,
            column_data_type: row.fieldDataType,
            column_length: String(row.fieldLength ?? 0),
            // true only for output-preview "Primary key" checkboxes; all other columns are false
            is_primary_key: Boolean(row.isPrimaryKey),
            delete_identifier: deleteIdentifierColumn === row.fieldName,
            created_date: createdDateColumn === row.fieldName,
            modified_date: modifiedDateColumn === row.fieldName,
          })),
          cdc_type: isNonDbDestination ? 'fullload' : (loadMode === 'full' ? 'fullload' : 'incremental'),
          scd_type: scdType,
        }
        await createAdpPhysicalTables(physicalTablesPayload)
      }

      const targetTableRows = await createAdpTargetTablesBatch(targetTablesPayload)
      const firstTargetRow = targetTableRows[0]

      if (configuringNodeId) {
        // If API didn't return IDs, show a warning but continue
        if (!firstTargetRow?.target_data_source_id || !firstTargetRow?.target_table_id) {
          console.warn(
            '[Target Tables Batch] Missing target IDs in API response. Column mapping may fail.'
          )
          toast.info(
            'Warning: Target table IDs not returned by API. You may need to manually check the mapping.'
          )
        }

        const sessionPayload = {
          nodeId: configuringNodeId,
          assetKind: 'destination' as const,
          dataSourceName: String(payload.source_name ?? destinationName ?? 'target_data_source'),
          dataSourceInstance: String(payload.type ?? normalizedType),
          targetDataSourceId: firstTargetRow?.target_data_source_id,
          targetTableId: firstTargetRow?.target_table_id,
          schemaName:
            typeof payload.schema === 'string' && payload.schema.trim()
              ? payload.schema
              : undefined,
          tableName,
          columns: selectedRows.map(row => ({
            name: row.fieldName,
            dataType: row.fieldDataType,
            selected: true,
            isBusinessPrimaryKey: row.isPrimaryKey,
          })),
        }

        console.log('[Session Update] Destination asset payload:', sessionPayload)
        upsertAssetSession(sessionPayload)
        if (graphSnapshot.edges.length > 0 && graphSnapshot.nodes.length > 0) {
          syncUpstreamSourcePrimaryKeyFromDestinationPreview({
            destinationNodeId: configuringNodeId,
            destinationFields: selectedRows.map(row => ({
              fieldName: row.fieldName,
              isPrimaryKey: row.isPrimaryKey,
            })),
            graphEdges: graphSnapshot.edges,
            graphNodes: graphSnapshot.nodes,
          })
        }
      }
      setOutputPreviewOpen(false)
      setColumnMappingOpen(true)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to persist target table metadata.')
    } finally {
      setIsPublishingTarget(false)
    }
  }, [
    buildDestinationPayload,
    buildPhysicalTablesDatabasePayload,
    createdDateColumn,
    deleteIdentifierColumn,
    destinationName,
    isRegisteredDestination,
    loadMode,
    modifiedDateColumn,
    normalizedType,
    outputPreviewRows,
    effectivePhysicalTableChoice,
    isNonDbDestination,
    configuringNodeId,
    graphSnapshot.edges,
    graphSnapshot.nodes,
    registeredDestination?.dataSourceType,
    registeredExistingTableIds,
    registeredTableSelectionMode,
    scdType,
    toast,
  ])

  const updateOutputPreviewRow = useCallback(
    (index: number, updater: (row: OutputPreviewRow) => OutputPreviewRow) => {
      setOutputPreviewRows(prev => prev.map((row, idx) => (idx === index ? updater(row) : row)))
    },
    []
  )

  const allOutputPreviewRowsSelected =
    outputPreviewRows.length > 0 && outputPreviewRows.every(row => row.isSelected)

  const handleToggleAllOutputPreviewRows = useCallback((checked: boolean) => {
    setOutputPreviewRows(prev =>
      prev.map(row => ({
        ...row,
        isSelected: checked,
        isPrimaryKey: checked ? row.isPrimaryKey : false,
      }))
    )
  }, [])

  const hasDynamicConfigFields = dynamicConfigFields.length > 0;
  const showDynamicConfigSkeleton =
    !isRegisteredDestination && !hasHydratedDraft && (isDynamicConfigLoading || !hasDynamicConfigFields);

  const handleChangeFormField = useCallback((key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleClearValidationError = useCallback((key: string) => {
    setValidationErrors(prev => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, []);

  const handleChangeResponseSelection = useCallback((key: string, value: string) => {
    setResponseSelections(prev => ({ ...prev, [key]: value }))
    setForm(prev => ({ ...prev, [key]: value }))
  }, []);

  const registeredDestinationFields = useMemo(() => {
    if (!registeredDestination) return null
    const fromList = buildConnectionDetailsFromRegisteredListItem({
      databaseName: registeredDestination.databaseName,
      username: registeredDestination.username,
      password: registeredDestination.password,
      endpoint: registeredDestination.endpoint ?? '',
    })
    const databaseName = firstNonEmptyTrimmed(
      registeredDestinationConnectionDetails?.database,
      registeredDestination.databaseName,
      fromList.database
    )
    const schemaName = firstUsableConnectionString(
      registeredDestinationConnectionDetails?.schema,
      fromList.schema,
      extractSchemaNameFromEndpoint(registeredDestination.endpoint ?? '')
    )
    return {
      destinationName: registeredDestination.connectionName ?? '',
      databaseName,
      schemaName: registeredInstanceSupportsSchema ? schemaName : '',
    }
  }, [registeredDestination, registeredDestinationConnectionDetails, registeredInstanceSupportsSchema])

  return (
    <aside className={styles.root} aria-label="Destination configuration details">
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <button
            type="button"
            className={styles.closeChevron}
            onClick={onClose}
            aria-label="Close"
          >
            <img
              src={chevronDoubleRightIcon}
              alt=""
              className={styles.closeChevronIcon}
              aria-hidden
            />
          </button>
          <h2 className={styles.title}>Configuration Details</h2>
        </div>

        {!shouldHideBackToList && onBackToChoose && (
          <button type="button" className={styles.backLink} onClick={onBackToChoose}>
            ← Back to destination list
          </button>
        )}

        {!isRegisteredDestination && (
          <>
            {!shouldHideBackToList && <div className={styles.divider} />}
            <p className={styles.subtitle}>
              Enter the configuration details for the destination.
            </p>
          </>
        )}
        <div className={styles.summaryCard} data-configuring-node={configuringNodeId ?? undefined}>
          <div className={styles.summaryIcon}>
            <img src={displayImage} alt={displayTitle} />
          </div>
          <div className={styles.summaryBody}>
            <div className={styles.summaryTitle}>{displayTitle}</div>
            <div className={styles.summaryType}>Destination Type : {displayType}</div>
            <button
              type="button"
              className={styles.prereqLink}
              onClick={() => setPrereqModalOpen(true)}
            >
              <MaterialIcon name="warning" size={16} aria-hidden />
              Prerequisites
            </button>
          </div>
        </div>

        <Accordion
          type="single"
          collapsible
          value={destinationDetailsAccordionValue}
          onValueChange={value =>
            setDestinationDetailsAccordionValue(
              value === 'destination-details' ? 'destination-details' : ''
            )
          }
          className={styles.destinationAccordion}
        >
          <AccordionItem value="destination-details" className={styles.accordion}>
            <AccordionTrigger className={styles.accordionHeader}>
              Destination Details
            </AccordionTrigger>
            <AccordionContent className={styles.accordionBody}>
              {isRegisteredDestination && registeredDestinationFields ? (
                <RegisteredDestinationTablesFields
                  key={`reg-dst-${registeredDestination.id}-${form.table_name ?? ''}-${registeredTableSelectionMode ?? 'none'}`}
                  targetDataSourceId={registeredDestination.id}
                  listDestination={registeredDestination}
                  restoredTableName={form.table_name?.trim() ?? ''}
                  restoredTableSelectionMode={registeredTableSelectionMode}
                  isTableSelectionLocked={registeredTableSelectionMode !== null}
                  destinationName={registeredDestinationFields.destinationName}
                  databaseName={registeredDestinationFields.databaseName}
                  schemaName={registeredDestinationFields.schemaName}
                  isSubmittingTarget={isSubmittingTarget}
                  onSaveTableSelection={(tableName, selectionMode, connectionDetails, existingTableIds) => {
                    setForm(prev => ({ ...prev, table_name: tableName.trim() }))
                    setRegisteredTableSelectionMode(selectionMode)
                    setRegisteredDestinationConnectionDetails(connectionDetails)
                    setRegisteredExistingTableIds(
                      selectionMode === 'existing' && existingTableIds ? existingTableIds : null
                    )
                    setDestinationDetailsAccordionValue('')
                    setLoadTypeOpen(isNonDbDestination ? false : true)
                    setOutputPreviewOpen(isNonDbDestination)
                    setColumnMappingOpen(false)
                  }}
                />
              ) : (
                <DynamicDestinationFields
                  dynamicConfigFields={dynamicConfigFields}
                  form={form}
                  destinationName={destinationName}
                  skipTestConnection={shouldSkipTestConnection}
                  validationErrors={validationErrors}
                  showDynamicConfigSkeleton={showDynamicConfigSkeleton}
                  isTesting={isTesting}
                  isSubmittingTarget={isSubmittingTarget}
                  hasRequiredTestSelections={hasRequiredTestSelections}
                  schemaTestFields={schemaTestFields}
                  responseSelections={responseSelections}
                  hasExcelSource={hasExcelSource}
                  onChangeFormField={handleChangeFormField}
                  onClearValidationError={handleClearValidationError}
                  onChangeResponseSelection={handleChangeResponseSelection}
                  onTest={() => void handleTest()}
                  onNext={() => void handleNext()}
                />
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className={styles.sectionHeader}>
          <div>
            <h3 className={styles.sectionTitle}>Output Validation &amp; Load Settings</h3>
            <p className={styles.sectionSubtitle}>
              Ensure proper column mapping and select a suitable loading strategy.
            </p>
          </div>
          <button
            type="button"
            className={styles.expandAllBtn}
            onClick={() => {
              const next = !allExpanded
              setLoadTypeOpen(isNonDbDestination ? false : next)
              setOutputPreviewOpen(next)
              setColumnMappingOpen(next)
            }}
          >
            <MaterialIcon name="unfold_more" size={16} />
            {allExpanded ? 'Collapse all' : 'Expand All'}
          </button>
        </div>

        {!isNonDbDestination && (
          <LoadTypeSettings
            isOpen={loadTypeOpen}
            isSaving={isSaving}
            loadMode={loadMode}
            scdType={scdType}
            deleteIdentifierColumn={deleteIdentifierColumn}
            createdDateColumn={createdDateColumn}
            modifiedDateColumn={modifiedDateColumn}
            outputPreviewColumnOptions={outputPreviewColumnOptions}
            onToggle={() => setLoadTypeOpen(v => !v)}
            onOpenPrerequisites={() => setPrereqModalOpen(true)}
            onSave={handleLoadTypeSave}
            onLoadModeChange={setLoadMode}
            onScdTypeChange={setScdType}
            onDeleteIdentifierColumnChange={setDeleteIdentifierColumn}
            onCreatedDateColumnChange={setCreatedDateColumn}
            onModifiedDateColumnChange={setModifiedDateColumn}
          />
        )}

        <div className={styles.accordion}>
          <button
            type="button"
            className={styles.accordionHeader}
            onClick={() => setOutputPreviewOpen(v => !v)}
            aria-expanded={outputPreviewOpen}
          >
            <span>Output preview</span>
            <MaterialIcon name={outputPreviewOpen ? 'expand_more' : 'chevron_right'} size={20} />
          </button>
          {outputPreviewOpen && (
            <div className={styles.accordionBody}>
              {showPhysicalTablePrompt && (
                <div
                  className={styles.physicalPromptRow}
                  role="radiogroup"
                  aria-label="Create physical table"
                >
                  <span>Do you want to create physical table ?</span>
                  <div className={styles.physicalPromptRadios}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={
                          configuringNodeId
                            ? `adp-create-physical-table-${configuringNodeId}`
                            : 'adp-create-physical-table'
                        }
                        checked={createPhysicalTableChoice === 'yes'}
                        onChange={() => setCreatePhysicalTableChoice('yes')}
                      />
                      yes
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={
                          configuringNodeId
                            ? `adp-create-physical-table-${configuringNodeId}`
                            : 'adp-create-physical-table'
                        }
                        checked={createPhysicalTableChoice === 'no'}
                        onChange={() => setCreatePhysicalTableChoice('no')}
                      />
                      No
                    </label>
                  </div>
                </div>
              )}
              <div className={styles.previewTableWrap}>
                <table className={`${styles.previewTable} ${styles.outputPreviewTable}`}>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={allOutputPreviewRowsSelected}
                          aria-label="Select all output preview rows"
                          onChange={event => handleToggleAllOutputPreviewRows(event.currentTarget.checked)}
                        />
                      </th>
                      <th>Field Name</th>
                      <th>Data Type</th>
                      <th>Length</th>
                      <th className={styles.previewPrimaryKeyCell}>Primary key</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outputPreviewRows.map((row, index) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={row.isSelected}
                            onChange={e => {
                              const checked = e.currentTarget.checked
                              updateOutputPreviewRow(index, current => ({
                                ...current,
                                isSelected: checked,
                                isPrimaryKey: checked ? current.isPrimaryKey : false,
                              }))
                            }}
                          />
                        </td>
                        <td>{row.fieldName}</td>
                        <td>
                          <select
                            className={styles.previewInput}
                            value={row.fieldDataType}
                            onChange={e => {
                              const nextType = normalizeOutputDataType(e.currentTarget.value)
                              updateOutputPreviewRow(index, current => ({
                                ...current,
                                fieldDataType: nextType,
                                fieldLength: defaultFieldLengthForDataType(nextType),
                              }))
                            }}
                          >
                            {OUTPUT_PREVIEW_DATA_TYPES.map(type => (
                              <option key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className={styles.previewInput}
                            value={row.fieldLength}
                            disabled={row.fieldDataType !== 'string'}
                            onChange={e => {
                              const nextLength = Number(e.currentTarget.value)
                              updateOutputPreviewRow(index, current => ({
                                ...current,
                                fieldLength:
                                  Number.isFinite(nextLength) && nextLength >= 0 ? nextLength : 0,
                              }))
                            }}
                          />
                        </td>
                        <td className={styles.previewPrimaryKeyCell}>
                          <input
                            type="checkbox"
                            checked={row.isPrimaryKey}
                            disabled={!row.isSelected}
                            onChange={e => {
                              const checked = e.currentTarget.checked
                              updateOutputPreviewRow(index, current => ({
                                ...current,
                                isPrimaryKey: checked,
                              }))
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                    {outputPreviewRows.length === 0 && (
                      <tr>
                        <td colSpan={5}>
                          No output columns found from upstream source/transformation nodes.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className={styles.previewFooter}>
                {/* <div className={styles.previewMeta}>Rows per page 10 | Page 1 of 10</div> */}
                {/* Pagination not implemented yet */}
                <Button
                  type="button"
                  className='ml-auto'
                  onClick={() => void handleOutputPreviewSave()}
                  disabled={isPublishingTarget || isSaving}
                >
                  {isPublishingTarget ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className={styles.accordion}>
          <button
            type="button"
            className={styles.accordionHeader}
            onClick={() => setColumnMappingOpen(v => !v)}
            aria-expanded={columnMappingOpen}
          >
            <span>Column mapping</span>
            <MaterialIcon name={columnMappingOpen ? 'expand_more' : 'chevron_right'} size={20} />
          </button>
          {columnMappingOpen && (
            <div className={styles.accordionBody}>
              <div className={styles.previewTableWrap}>
                <table className={`${styles.previewTable} ${styles.columnMappingTable}`}>
                  <thead>
                    <tr>
                      <th>Source Name</th>
                      <th>Source Column</th>
                      <th>Source Datatype</th>
                      <th>Physical column</th>
                      <th>Destination Datatype</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columnMappingRows.map((row, index) => (
                      <tr key={`${row.sourceColumn}-${row.destinationColumn}-${index}`}>
                        <td>{row.sourceName || '—'}</td>
                        <td>{row.sourceColumn || '—'}</td>
                        <td>
                          <span className={styles.datatypePill}>{row.sourceDataType || '—'}</span>
                        </td>
                        <td>
                          <select
                            className={`${styles.previewInput} ${styles.columnMappingSelect}`}
                            defaultValue={row.destinationColumn || ''}
                          >
                            <option value="">Select column</option>
                            {destinationColumnOptions.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <span className={styles.datatypePill}>
                            {row.destinationDataType || row.sourceDataType
                              ? normalizeOutputDataType(
                                row.destinationDataType || row.sourceDataType
                              )
                              : '—'}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={styles.iconDeleteBtn}
                            aria-label="Delete row"
                          >
                            <MaterialIcon name="delete" size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {columnMappingRows.length === 0 && (
                      <tr>
                        <td colSpan={6}>No source/transformation session data found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className={styles.previewFooter}>
                  {/* <div className={styles.previewMeta}>Rows per page 10 | Page 1 of 10</div> */}
                  <Button
                    type="button"
                    className='ml-auto'
                    onClick={() => void handleColumnMappingSave()}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <PrerequisitesModal
        open={prereqModalOpen}
        onOpenChange={setPrereqModalOpen}
        connectorName={displayTitle}
        prerequisite={prerequisiteData}
      />
    </aside>
  )
}
